import { useState } from 'react';
import { Icon } from '../../components/Icon.tsx';
import { Dialog } from '../../components/Dialog.tsx';
import { useCategories, useZoneAdmin, useZones, useZoneUsers } from '../../features/zone/api.ts';
import { zoneErrorMessage } from '../../features/zone/errors.ts';
import type { Category, Zone } from '../../features/zone/types.ts';

type Tab = 'zones' | 'categories';
type Target =
  | { kind: 'add' }
  | { kind: 'menu'; item: Zone | Category }
  | { kind: 'rename'; item: Zone | Category }
  | { kind: 'councillors'; item: Zone };

const tabClass = (on: boolean): string =>
  `-mb-px border-b-2 px-3 py-2 text-sm font-medium transition-colors ${
    on ? 'border-primary text-primary' : 'border-transparent text-muted-foreground hover:text-foreground'
  }`;

const menuItem = 'option-row';

function NameForm({
  initial, submitLabel, pending, error, onSubmit,
}: { initial: string; submitLabel: string; pending: boolean; error: string | null; onSubmit: (name: string) => void }): JSX.Element {
  const [name, setName] = useState(initial);
  const trimmed = name.trim();
  return (
    <form
      className="space-y-4"
      onSubmit={(e) => { e.preventDefault(); if (trimmed) onSubmit(trimmed); }}
    >
      <div>
        <label className="field-label" htmlFor="zone-name">Name</label>
        <input id="zone-name" className="input" value={name} maxLength={100} autoFocus onChange={(e) => setName(e.target.value)} />
      </div>
      {error && <p className="text-sm text-destructive" role="alert">{error}</p>}
      <div className="flex justify-end">
        <button type="submit" className="btn-primary" disabled={!trimmed || pending}>{submitLabel}</button>
      </div>
    </form>
  );
}

function CouncillorPicker({
  zone, onDone,
}: { zone: Zone; onDone: () => void }): JSX.Element {
  const { data: users, isLoading } = useZoneUsers('COUNCILLOR');
  const { setCouncillors } = useZoneAdmin();
  // Seeded once the picker list loads, and only with ids the picker offers (the server rejects others).
  const [selected, setSelected] = useState<Set<string> | null>(null);
  const current = selected ?? new Set((users ?? []).filter((u) => zone.councillors.some((c) => c.id === u.id)).map((u) => u.id));
  const [search, setSearch] = useState('');

  const q = search.trim().toLowerCase();
  const list = (users ?? []).filter((u) => !q || u.name.toLowerCase().includes(q));
  const toggle = (id: string): void =>
    setSelected(() => { const n = new Set(current); if (n.has(id)) n.delete(id); else n.add(id); return n; });

  if (isLoading) return <p className="status-label" role="status" aria-live="polite">Loading…</p>;
  if (!users || users.length === 0) {
    return <p className="text-sm text-muted-foreground">Give people the Councillor zone role in Wapplify Office first.</p>;
  }

  return (
    <div className="space-y-4">
      <input
        type="search"
        className="input"
        value={search}
        placeholder="Search councillors"
        aria-label="Search councillors"
        onChange={(e) => setSearch(e.target.value)}
      />
      <ul className="-mx-2 max-h-[50vh] overflow-y-auto">
        {list.map((u) => (
          <li key={u.id}>
            <label className="option-row cursor-pointer">
              <input type="checkbox" className="size-5 accent-[hsl(var(--primary))]" checked={current.has(u.id)} onChange={() => toggle(u.id)} />
              <span className="min-w-0 truncate">{u.name}</span>
            </label>
          </li>
        ))}
        {list.length === 0 && <li className="px-3 py-3 text-sm text-muted-foreground">No councillors match.</li>}
      </ul>
      {setCouncillors.isError && <p className="text-sm text-destructive" role="alert">{zoneErrorMessage(setCouncillors.error)}</p>}
      <div className="flex justify-end">
        <button
          type="button"
          className="btn-primary"
          disabled={setCouncillors.isPending}
          onClick={() => setCouncillors.mutate({ id: zone.id, userIds: [...current] }, { onSuccess: onDone })}
        >
          Save
        </button>
      </div>
    </div>
  );
}

function ItemRow({
  name, sub, active, onMenu,
}: { name: string; sub?: string; active: boolean; onMenu: () => void }): JSX.Element {
  return (
    <li className="flex items-center gap-3 border-b border-[hsl(var(--border)/0.6)] px-4 py-3 last:border-b-0 sm:px-5">
      <div className="min-w-0 flex-1">
        <div className={`truncate text-[0.95rem] font-medium ${active ? 'text-ink' : 'text-muted-foreground'}`}>{name}</div>
        {sub !== undefined && <div className="truncate text-sm text-muted-foreground">{sub}</div>}
      </div>
      <span className={`shrink-0 text-xs font-medium ${active ? 'text-[hsl(var(--success))]' : 'text-muted-foreground'}`}>
        {active ? 'Active' : 'Inactive'}
      </span>
      <button type="button" className="btn-ghost btn-icon" aria-label={`Actions for ${name}`} onClick={onMenu}>
        <Icon name="more_vert" size={20} />
      </button>
    </li>
  );
}

export function ZoneSettings(): JSX.Element {
  const [tab, setTab] = useState<Tab>('zones');
  const [target, setTarget] = useState<Target | null>(null);
  const zones = useZones(true);
  const categories = useCategories(true);
  const admin = useZoneAdmin();

  const isZones = tab === 'zones';
  const query = isZones ? zones : categories;
  const items: Array<Zone | Category> = (isZones ? zones.data : categories.data) ?? [];
  const noun = isZones ? 'zone' : 'category';

  const close = (): void => {
    setTarget(null);
    admin.createZone.reset(); admin.updateZone.reset(); admin.createCategory.reset(); admin.updateCategory.reset();
  };
  const switchTab = (t: Tab): void => { setTab(t); close(); };

  const create = isZones ? admin.createZone : admin.createCategory;
  const update = isZones ? admin.updateZone : admin.updateCategory;
  const mutErr = (m: { isError: boolean; error: unknown }): string | null => (m.isError ? zoneErrorMessage(m.error) : null);

  const item = target && target.kind !== 'add' ? target.item : null;

  return (
    <div className="page space-y-5 md:space-y-6">
      <header className="flex flex-wrap items-center justify-between gap-x-6 gap-y-3">
        <h1 className="text-3xl leading-tight tracking-tight md:text-[2.5rem]">Zone settings</h1>
        <button type="button" className="btn-primary" onClick={() => setTarget({ kind: 'add' })}>
          <Icon name="add" size={18} /> {isZones ? 'Add zone' : 'Add category'}
        </button>
      </header>

      <div className="flex gap-2 border-b border-border">
        <button type="button" aria-pressed={isZones} className={tabClass(isZones)} onClick={() => switchTab('zones')}>Zones</button>
        <button type="button" aria-pressed={!isZones} className={tabClass(!isZones)} onClick={() => switchTab('categories')}>Categories</button>
      </div>

      {query.isLoading ? (
        <p className="status-label" role="status" aria-live="polite">Loading…</p>
      ) : query.isError ? (
        <p className="text-sm text-destructive" role="alert">Could not load {noun}s. Please try again.</p>
      ) : items.length === 0 ? (
        <p className="py-12 text-center text-sm text-muted-foreground">No {noun}s yet.</p>
      ) : (
        <ul className="overflow-hidden rounded-3xl border border-[hsl(var(--border)/0.7)]">
          {items.map((it) => (
            <ItemRow
              key={it.id}
              name={it.name}
              sub={isZones ? ((it as Zone).councillors.map((c) => c.name).join(', ') || 'No councillors') : undefined}
              active={it.isActive}
              onMenu={() => setTarget({ kind: 'menu', item: it })}
            />
          ))}
        </ul>
      )}

      <Dialog open={target?.kind === 'add'} onClose={close} title={isZones ? 'Add zone' : 'Add category'}>
        <NameForm
          initial="" submitLabel="Add" pending={create.isPending} error={mutErr(create)}
          onSubmit={(name) => create.mutate({ name }, { onSuccess: close })}
        />
      </Dialog>

      <Dialog open={target?.kind === 'rename'} onClose={close} title={`Rename ${noun}`}>
        {item && (
          <NameForm
            initial={item.name} submitLabel="Save" pending={update.isPending} error={mutErr(update)}
            onSubmit={(name) => update.mutate({ id: item.id, name }, { onSuccess: close })}
          />
        )}
      </Dialog>

      <Dialog open={target?.kind === 'councillors'} onClose={close} title={item ? `Councillors — ${item.name}` : 'Councillors'}>
        {target?.kind === 'councillors' && <CouncillorPicker key={target.item.id} zone={target.item} onDone={close} />}
      </Dialog>

      <Dialog open={target?.kind === 'menu'} onClose={close} title={item?.name ?? ''}>
        {item && (
          <div className="-mx-2">
            <button type="button" className={menuItem} onClick={() => setTarget({ kind: 'rename', item })}>
              <Icon name="edit" size={20} /> Rename
            </button>
            {isZones && (
              <button type="button" className={menuItem} onClick={() => setTarget({ kind: 'councillors', item: item as Zone })}>
                <Icon name="group" size={20} /> Councillors
              </button>
            )}
            <button
              type="button"
              className={menuItem}
              disabled={update.isPending}
              onClick={() => update.mutate({ id: item.id, isActive: !item.isActive }, { onSuccess: close })}
            >
              <Icon name={item.isActive ? 'visibility_off' : 'visibility'} size={20} />
              {item.isActive ? 'Deactivate' : 'Activate'}
            </button>
            {mutErr(update) && <p className="px-3 pt-2 text-sm text-destructive" role="alert">{mutErr(update)}</p>}
          </div>
        )}
      </Dialog>
    </div>
  );
}
